// URLs de la API para el sistema Laboratorio Central (Vite env vars)
const localApiUrl = import.meta.env.VITE_API_URL_LOCAL || "http://ch-laboratorio.test/api/v1/";
const productionApiUrl = import.meta.env.VITE_API_URL_PRODUCTION || "https://laboratorioapi.digitalsis.com.ar/api/v1/";

export function serverPath(){
    const hostname = window.location.hostname;
    
    // Desarrollo local: si estamos en localhost o 127.0.0.1 (Vite dev server), usar URL completa
    if(hostname === "localhost" || hostname === "127.0.0.1") {
        return localApiUrl;
    }
    
    // Desarrollo local: si estamos en un dominio .test, usar URL relativa si es el mismo origen
    if(hostname.includes(".test")) {
        const currentOrigin = window.location.origin;
        if(currentOrigin !== `http://${hostname}` && currentOrigin !== `https://${hostname}`) {
            return localApiUrl;
        }
        return "/api/v1/";
    }
    
    // Producción: usar la URL configurada (cada proyecto debe definir productionApiUrl o su lógica)
    if(productionApiUrl) {
        return productionApiUrl;
    }
    
    return "/api/v1/";
}

// Función para detectar si un error es de token vencido
function isTokenExpiredError(errorMessage) {
    if (!errorMessage) return false;
    const errorLower = errorMessage.toLowerCase();
    return errorLower.includes('token vencido') || 
           errorLower.includes('token inválido') || 
           errorLower.includes('token invalido') ||
           errorLower.includes('token requerido') ||
           errorLower.includes('unauthorized');
}

// Función para desloguear al usuario cuando el token está vencido
function handleTokenExpired() {
    // Limpiar el localStorage
    const constantes = {
        usuario: "ch-laboratorio-central-usuario"
    };
    window.localStorage.removeItem(constantes.usuario);
    
    // Redirigir al login si no estamos ya ahí
    // Usar hash para HashRouter
    const currentPath = window.location.hash || window.location.pathname;
    if (!currentPath.includes('/login')) {
        window.location.hash = '#/login';
    }
}

/** Laravel suele devolver `message` genérico y el detalle en `errors` (422). */
const LARAVEL_GENERIC_VALIDATION = new Set([
    'The given data was invalid.',
    'Los datos proporcionados no son válidos.',
]);

function friendlyFieldLabel(field) {
    const direct = {
        cuil: 'CUIT',
        nombre: 'Nombre',
        id_determinacion_grupo: 'Sector',
        detalles: 'Detalles',
        codigo: 'Código',
        arancel: 'Arancel',
        categoria_descripcion: 'Categoría / descripción',
    };
    if (direct[field]) return direct[field];
    const det = field.match(/^detalles\.(\d+)\.(id_codigo|cantidad)$/);
    if (det) {
        const n = parseInt(det[1], 10) + 1;
        return det[2] === 'id_codigo' ? `Detalle (fila ${n}) · código` : `Detalle (fila ${n}) · cantidad`;
    }
    return field;
}

/**
 * Mensaje legible para respuestas JSON de error (sobre todo validación 422).
 */
function messageFromJsonError(errJson, httpStatus) {
    if (!errJson) return 'Error en la petición';
    if (httpStatus === 422 && errJson.errors && typeof errJson.errors === 'object' && Object.keys(errJson.errors).length > 0) {
        return Object.entries(errJson.errors)
            .map(([field, messages]) => {
                const msgs = Array.isArray(messages) ? messages.join(' ') : String(messages);
                return `${friendlyFieldLabel(field)}: ${msgs}`;
            })
            .join(' · ');
    }
    // Laravel / API: mensaje de excepción o error (5xx, 4xx no validación)
    if (httpStatus >= 400 && typeof errJson.message === 'string' && errJson.message.trim()) {
        const m = errJson.message.trim();
        if (!LARAVEL_GENERIC_VALIDATION.has(m)) {
            return m;
        }
    }
    if (typeof errJson.error === 'string' && errJson.error) {
        return errJson.error;
    }
    if (errJson.error && typeof errJson.error === 'object' && errJson.error.message) {
        return errJson.error.message;
    }
    if (errJson.message && !LARAVEL_GENERIC_VALIDATION.has(errJson.message)) {
        return errJson.message;
    }
    if (httpStatus === 422) {
        return 'Los datos enviados no pasaron la validación.';
    }
    if (httpStatus >= 500) {
        return 'Error en el servidor. Si persiste, revisá los logs o contactá al administrador.';
    }
    return errJson.message || 'Error en la petición';
}

export async function postData(name = '', data = {}, token = null) {
    const response = { data: {}, error: "", warning: "", filename: "" };

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        //'Content-Type': 'application/x-www-form-urlencoded',
    };

    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        /*mode: 'no-cors', // no-cors, *cors, same-origin
        cache: 'no-cache', // *default, no-cache, reload, force-cache, only-if-cached
        credentials: 'same-origin', // include, *same-origin, omit
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'no-referrer', // no-referrer, *client*/
        method: 'POST', // *GET, POST, PUT, DELETE, etc.
        headers: headers,
        body: JSON.stringify(data) // body data type must match "Content-Type" header
    })
        .then((res) => {
            var t_contentDisposition = res.headers.get("content-disposition");
            if(t_contentDisposition!=null && t_contentDisposition.includes("attachment")){
                response.filename = t_contentDisposition.split(";")[1].split('=')[1];
                return res.blob();
            }
            else{
                // Manejar respuestas sin contenido (204 No Content) antes de verificar res.ok
                if (res.status === 204) {
                    return null;
                }
                
                if (!res.ok) {
                    // Intentar parsear el error, pero manejar el caso de respuesta vacía
                    const contentType = res.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        return res.json().then(err => {
                            const errorMsg = messageFromJsonError(err, res.status);

                            // Verificar si es error de token vencido
                            if (isTokenExpiredError(errorMsg) || res.status === 401) {
                                handleTokenExpired();
                            }

                            // Crear un error con el mensaje correcto
                            const error = new Error(errorMsg);
                            error.status = res.status;
                            throw error;
                        }).catch((parseError) => {
                            // Si hay un error al parsear JSON, pero ya tenemos un mensaje de error válido, usarlo
                            // Verificar si el error tiene un mensaje válido (no es un error de parseo JSON)
                            if (parseError.message && 
                                parseError.message !== 'Error en la petición' && 
                                !parseError.message.includes('JSON') &&
                                !parseError.message.includes('Unexpected token') &&
                                parseError.message !== `Error ${res.status}: ${res.statusText}`) {
                                // El error ya tiene un mensaje válido, propagarlo
                                throw parseError;
                            }
                            // Si llegamos aquí, es un error de parseo JSON real
                            if (res.status === 401) {
                                handleTokenExpired();
                            }
                            // Solo usar statusText como último recurso
                            const error = new Error(`Error ${res.status}: ${res.statusText}`);
                            error.status = res.status;
                            throw error;
                        });
                    } else {
                        if (res.status === 401) {
                            handleTokenExpired();
                        }
                        const error = new Error(`Error ${res.status}: ${res.statusText}`);
                        error.status = res.status;
                        throw error;
                    }
                }
                
                // Verificar si hay contenido antes de parsear JSON
                const contentType = res.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return res.json();
                }
                return null;
            }
            
        })
        .then((data) => {
            response.data = data?.data ? data.data : (data ? data : []);
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;

            // Verificar si es error de token vencido
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch(t_error){
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        // Mantener el mensaje de error original
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}

export async function getData(name = '', token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'GET',
        headers: headers,
    })
        .then((res) => {
            if (!res.ok) {
                return res.json().then(err => {
                    const errorMsg = err.error?.message || err.error || 'Error en la petición';
                    // Verificar si es error de token vencido
                    if (isTokenExpiredError(errorMsg) || res.status === 401) {
                        handleTokenExpired();
                    }
                    throw new Error(errorMsg);
                }).catch(() => {
                    if (res.status === 401) {
                        handleTokenExpired();
                    }
                    throw new Error(`Error ${res.status}: ${res.statusText}`);
                });
            }
            return res.json();
        })
        .then((data) => {
            response.data = data.data ? data.data : (data ? data : {});
            response.error = data.error ? data.error : "";
            response.warning = data.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;
            // Verificar si es error de token vencido
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch(t_error){
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        // Mantener el mensaje de error original
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}

export async function getDataFull(name = '', token = null) {
    const response = { data: {}, error: "", warning: "", meta: {} };

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'GET',
        headers: headers,
    })
        .then((res) => {
            if (!res.ok) {
                // Intentar parsear el error, pero manejar el caso de respuesta vacía
                const contentType = res.headers.get('content-type');
                if (contentType && contentType.includes('application/json')) {
                    return res.json().then(err => {
                        const errorMsg = messageFromJsonError(err, res.status);

                        // Verificar si es error de token vencido
                        if (isTokenExpiredError(errorMsg) || res.status === 401) {
                            handleTokenExpired();
                        }

                        // Crear un error con el mensaje correcto
                        const error = new Error(errorMsg);
                        error.status = res.status;
                        throw error;
                    }).catch((parseError) => {
                        // Si hay un error al parsear JSON, pero ya tenemos un mensaje de error válido, usarlo
                        if (parseError.message && 
                            parseError.message !== 'Error en la petición' && 
                            !parseError.message.includes('JSON') &&
                            !parseError.message.includes('Unexpected token') &&
                            parseError.message !== `Error ${res.status}: ${res.statusText}`) {
                            // El error ya tiene un mensaje válido, propagarlo
                            throw parseError;
                        }
                        // Si llegamos aquí, es un error de parseo JSON real
                        if (res.status === 401) {
                            handleTokenExpired();
                        }
                        // Solo usar statusText como último recurso
                        const error = new Error(`Error ${res.status}: ${res.statusText}`);
                        error.status = res.status;
                        throw error;
                    });
                } else {
                    if (res.status === 401) {
                        handleTokenExpired();
                    }
                    const error = new Error(`Error ${res.status}: ${res.statusText}`);
                    error.status = res.status;
                    throw error;
                }
            }
            
            // Verificar si hay contenido antes de parsear JSON
            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data.data ? data.data : (data ? data : {});
            response.meta = data.meta ? data.meta : {};
            response.error = data.error ? data.error : "";
            response.warning = data.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;
            // Verificar si es error de token vencido
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch(t_error){
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        // Mantener el mensaje de error original
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}

export async function putData(name = '', data = {}, token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify(data)
    })
        .then((res) => {
            // Manejar respuestas sin contenido (204 No Content) antes de verificar res.ok
            if (res.status === 204) {
                return null;
            }
            
            if (!res.ok) {
                const contentTypeErr = res.headers.get('content-type');
                if (contentTypeErr && contentTypeErr.includes('application/json')) {
                    return res.json().then(err => {
                        const errorMsg = messageFromJsonError(err, res.status);
                        if (isTokenExpiredError(errorMsg) || res.status === 401) {
                            handleTokenExpired();
                        }
                        const error = new Error(errorMsg);
                        error.status = res.status;
                        throw error;
                    }).catch((parseError) => {
                        if (parseError.message &&
                            parseError.message !== 'Error en la petición' &&
                            !parseError.message.includes('JSON') &&
                            !parseError.message.includes('Unexpected token') &&
                            parseError.message !== `Error ${res.status}: ${res.statusText}`) {
                            throw parseError;
                        }
                        if (res.status === 401) {
                            handleTokenExpired();
                        }
                        const error = new Error(`Error ${res.status}: ${res.statusText}`);
                        error.status = res.status;
                        throw error;
                    });
                }
                if (res.status === 401) {
                    handleTokenExpired();
                }
                const error = new Error(`Error ${res.status}: ${res.statusText}`);
                error.status = res.status;
                throw error;
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data?.data ? data.data : (data ? data : []);
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch (t_error) {
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}

export async function deleteData(name = '', token = null) {
    const response = { data: {}, error: "", warning: "" };

    const headers = {
        'Content-Type': 'application/json',
        Accept: 'application/json',
    };

    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'DELETE',
        headers: headers,
    })
        .then((res) => {
            // Manejar respuestas sin contenido (204 No Content) antes de verificar res.ok
            if (res.status === 204) {
                return null;
            }
            
            if (!res.ok) {
                const contentTypeErr = res.headers.get('content-type');
                if (contentTypeErr && contentTypeErr.includes('application/json')) {
                    return res.json().then(err => {
                        const errorMsg = messageFromJsonError(err, res.status);
                        if (isTokenExpiredError(errorMsg) || res.status === 401) {
                            handleTokenExpired();
                        }
                        const error = new Error(errorMsg);
                        error.status = res.status;
                        throw error;
                    }).catch((parseError) => {
                        if (parseError.message &&
                            parseError.message !== 'Error en la petición' &&
                            !parseError.message.includes('JSON') &&
                            !parseError.message.includes('Unexpected token') &&
                            parseError.message !== `Error ${res.status}: ${res.statusText}`) {
                            throw parseError;
                        }
                        if (res.status === 401) {
                            handleTokenExpired();
                        }
                        const error = new Error(`Error ${res.status}: ${res.statusText}`);
                        error.status = res.status;
                        throw error;
                    });
                }
                if (res.status === 401) {
                    handleTokenExpired();
                }
                const error = new Error(`Error ${res.status}: ${res.statusText}`);
                error.status = res.status;
                throw error;
            }

            const contentType = res.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return res.json();
            }
            return null;
        })
        .then((data) => {
            response.data = data?.data ? data.data : (data ? data : {});
            response.error = data?.error ? data.error : "";
            response.warning = data?.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch (t_error) {
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}

export async function postFiles(name = '', data = {}, files = {}, token = null) {
    const response = { data: {}, error: "", warning: "" };

    const keysData = Object.keys(data);
    const keysFiles = Object.keys(files);
    const formData = new FormData();
    
    for (var i = 0; i < keysData.length; i++) {
        formData.append(keysData[i], data[keysData[i]]);
    }
    for (i = 0; i < keysFiles.length; i++) {
        for (var i2 = 0; i2 < files[keysFiles[i]].length; i2++) {
            if (files[keysFiles[i]][i2].file !== null)
                formData.append(keysFiles[i] + "_" + i2, files[keysFiles[i]][i2].file);
        }
        formData.append(keysFiles[i], JSON.stringify(files[keysFiles[i]]));
    }

    const headers = {};
    // Agregar token de autenticación si está presente
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    await fetch(serverPath() + name, {
        method: 'POST',
        headers: headers,
        body: formData
    })
        .then((res) => {
            if (!res.ok) {
                return res.json().then(err => {
                    const errorMsg = err.error?.message || err.error || 'Error en la petición';
                    // Verificar si es error de token vencido
                    if (isTokenExpiredError(errorMsg) || res.status === 401) {
                        handleTokenExpired();
                    }
                    throw new Error(errorMsg);
                }).catch(() => {
                    if (res.status === 401) {
                        handleTokenExpired();
                    }
                    throw new Error(`Error ${res.status}: ${res.statusText}`);
                });
            }
            return res.json();
        })
        .then((data) => {
            response.data = data.data ? data.data : [];
            response.error = data.error ? data.error : "";
            response.warning = data.warning ? data.warning : "";
        })
        .catch((error) => {
            var t_error = error.message;
            // Verificar si es error de token vencido
            if (isTokenExpiredError(t_error)) {
                handleTokenExpired();
                t_error = "Sesión expirada. Por favor, inicia sesión nuevamente.";
            } else {
                switch(t_error){
                    case "Failed to fetch":
                        t_error = "Error al conectar con el servidor";
                        break;
                    default:
                        // Mantener el mensaje de error original
                        break;
                }
            }
            response.error = t_error;
        });

    return response;
}
